import { useState, useCallback } from "react";
import { CONFIG_NEGOCIO_DEFAULT } from "./useConfiguracionNegocio";

function useCalculoEnvio(
  subtotalComida,
  promociones = [],
  configuracionNegocio = CONFIG_NEGOCIO_DEFAULT,
) {
  const [costoEnvio, setCostoEnvio] = useState(0);
  const [distanciaKm, setDistanciaKm] = useState(0);
  const [mensajeEnvio, setMensajeEnvio] = useState("");
  const [esRutaValida, setEsRutaValida] = useState(false);
  const [fueraDeRango, setFueraDeRango] = useState(false);

  const latRestaurante = Number(
    configuracionNegocio.latNegocio ?? CONFIG_NEGOCIO_DEFAULT.latNegocio,
  );

  const lngRestaurante = Number(
    configuracionNegocio.lngNegocio ?? CONFIG_NEGOCIO_DEFAULT.lngNegocio,
  );

  const radioEntregaKm = Number(
    configuracionNegocio.radioEntregaKm ??
      CONFIG_NEGOCIO_DEFAULT.radioEntregaKm,
  );

  const costoEnvioBase = Number(
    configuracionNegocio.costoEnvioBase ??
      CONFIG_NEGOCIO_DEFAULT.costoEnvioBase,
  );

  const costoEnvioPorKm = Number(
    configuracionNegocio.costoEnvioPorKm ??
      CONFIG_NEGOCIO_DEFAULT.costoEnvioPorKm,
  );

  const kmIncluidosEnvio = Number(
    configuracionNegocio.kmIncluidosEnvio ??
      CONFIG_NEGOCIO_DEFAULT.kmIncluidosEnvio,
  );

  const calcularDistanciaSatelital = (lat1, lon1, lat2, lon2) => {
    const R = 6371;

    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  const redondearDistancia = (distKm) => {
    const numero = Number(distKm);

    if (Number.isNaN(numero)) return null;

    if (numero < 1) {
      return parseFloat(numero.toFixed(2));
    }

    return parseFloat(numero.toFixed(1));
  };

  const aplicarReglasEnvio = useCallback(
    (distKm, fuente = "google") => {
      const distanciaRedondeada = redondearDistancia(distKm);

      if (distanciaRedondeada === null) {
        setCostoEnvio(0);
        setDistanciaKm(0);
        setEsRutaValida(false);
        setFueraDeRango(false);
        setMensajeEnvio("⚠️ No se pudo calcular una distancia válida.");
        return;
      }

      setDistanciaKm(distanciaRedondeada);
      setEsRutaValida(true);

      if (distanciaRedondeada > radioEntregaKm) {
        setFueraDeRango(true);
        setCostoEnvio(0);

        setMensajeEnvio(
          `❌ Lo sentimos, esta dirección está a ${distanciaRedondeada} km y nuestro límite máximo es ${radioEntregaKm} km.`,
        );

        return;
      }

      setFueraDeRango(false);

      const promoEnvioGratis = promociones.find(
        (p) => p.tipo === "envio_gratis",
      );

      if (
        promoEnvioGratis &&
        subtotalComida >= Number(promoEnvioGratis.minimo || 0)
      ) {
        setCostoEnvio(0);

        setMensajeEnvio(
          `🎉 ¡Envío GRATIS por compra mayor a $${promoEnvioGratis.minimo}!`,
        );

        return;
      }

      let totalEnvio = Number.isNaN(costoEnvioBase) ? 0 : costoEnvioBase;

      if (
        distanciaRedondeada > kmIncluidosEnvio &&
        !Number.isNaN(costoEnvioPorKm) &&
        costoEnvioPorKm > 0
      ) {
        const kmCobrables = distanciaRedondeada - kmIncluidosEnvio;
        const kmCobrablesRedondeados = Math.ceil(kmCobrables * 10) / 10;

        totalEnvio += kmCobrablesRedondeados * costoEnvioPorKm;
      }

      totalEnvio = parseFloat(totalEnvio.toFixed(2));

      setCostoEnvio(totalEnvio);

      const textoFuente =
        fuente === "directions"
          ? "ruta Google"
          : fuente === "matrix"
            ? "ruta Google"
            : "distancia directa";

      setMensajeEnvio(
        `🛵 Distancia calculada: ${distanciaRedondeada} km (${textoFuente})`,
      );
    },
    [
      subtotalComida,
      promociones,
      radioEntregaKm,
      costoEnvioBase,
      costoEnvioPorKm,
      kmIncluidosEnvio,
    ],
  );

  const resetearEnvio = useCallback(() => {
    setCostoEnvio(0);
    setDistanciaKm(0);
    setMensajeEnvio("");
    setEsRutaValida(false);
    setFueraDeRango(false);
  }, []);

  const calcularConDistanceMatrix = useCallback(
    ({ origenGoogle, destinoGoogle, distanciaSatelital }) => {
      if (
        !window.google ||
        !window.google.maps ||
        !window.google.maps.DistanceMatrixService
      ) {
        aplicarReglasEnvio(distanciaSatelital, "directa");
        return;
      }

      try {
        const service = new window.google.maps.DistanceMatrixService();

        service.getDistanceMatrix(
          {
            origins: [origenGoogle],
            destinations: [destinoGoogle],
            travelMode: window.google.maps.TravelMode.DRIVING,
            unitSystem: window.google.maps.UnitSystem.METRIC,
          },
          (response, status) => {
            if (status !== "OK") {
              console.warn("DistanceMatrix falló:", status);
              aplicarReglasEnvio(distanciaSatelital, "directa");
              return;
            }

            const elemento = response?.rows?.[0]?.elements?.[0];

            if (!elemento || elemento.status !== "OK") {
              console.warn("DistanceMatrix elemento inválido:", elemento);
              aplicarReglasEnvio(distanciaSatelital, "directa");
              return;
            }

            const distanciaMetros = elemento.distance?.value;

            if (!distanciaMetros) {
              aplicarReglasEnvio(distanciaSatelital, "directa");
              return;
            }

            const distanciaMatrixKm = distanciaMetros / 1000;

            if (
              distanciaSatelital <= 0.25 &&
              distanciaMatrixKm > distanciaSatelital * 4
            ) {
              aplicarReglasEnvio(distanciaSatelital, "directa");
              return;
            }

            aplicarReglasEnvio(distanciaMatrixKm, "matrix");
          },
        );
      } catch (error) {
        console.error("Error usando DistanceMatrix:", error);
        aplicarReglasEnvio(distanciaSatelital, "directa");
      }
    },
    [aplicarReglasEnvio],
  );

  const calcularRuta = useCallback(
    async (destinoStr, latDest, lngDest) => {
      if (!destinoStr) {
        resetearEnvio();
        return;
      }

      if (
        latDest === null ||
        latDest === undefined ||
        lngDest === null ||
        lngDest === undefined
      ) {
        resetearEnvio();

        setMensajeEnvio(
          "⚠️ No se pudieron obtener coordenadas válidas de la dirección.",
        );

        return;
      }

      if (
        Number.isNaN(latRestaurante) ||
        Number.isNaN(lngRestaurante) ||
        latRestaurante === null ||
        lngRestaurante === null
      ) {
        resetearEnvio();

        setMensajeEnvio(
          "⚠️ No se configuraron coordenadas válidas del negocio.",
        );

        return;
      }

      const latDestinoNumero = Number(latDest);
      const lngDestinoNumero = Number(lngDest);

      const distanciaSatelital = calcularDistanciaSatelital(
        latRestaurante,
        lngRestaurante,
        latDestinoNumero,
        lngDestinoNumero,
      );

      console.log("DEBUG ENVÍO:", {
        origen: {
          latRestaurante,
          lngRestaurante,
        },
        destino: {
          latDest: latDestinoNumero,
          lngDest: lngDestinoNumero,
          destinoStr,
        },
        distanciaSatelital,
      });

      // Si está extremadamente cerca, no dejamos que Google invente una ruta larga.
      if (distanciaSatelital <= 0.05) {
        aplicarReglasEnvio(distanciaSatelital, "directa");
        return;
      }

      if (!window.google || !window.google.maps || !window.google.maps.LatLng) {
        aplicarReglasEnvio(distanciaSatelital, "directa");
        return;
      }

      const origenGoogle = new window.google.maps.LatLng(
        latRestaurante,
        lngRestaurante,
      );

      const destinoGoogle = new window.google.maps.LatLng(
        latDestinoNumero,
        lngDestinoNumero,
      );

      if (!window.google.maps.DirectionsService) {
        calcularConDistanceMatrix({
          origenGoogle,
          destinoGoogle,
          distanciaSatelital,
        });
        return;
      }

      try {
        const directionsService = new window.google.maps.DirectionsService();

        directionsService.route(
          {
            origin: origenGoogle,
            destination: destinoGoogle,
            travelMode: window.google.maps.TravelMode.DRIVING,
          },
          (result, status) => {
            if (status !== "OK") {
              console.warn("DirectionsService falló:", status);

              calcularConDistanceMatrix({
                origenGoogle,
                destinoGoogle,
                distanciaSatelital,
              });

              return;
            }

            const ruta = result?.routes?.[0];

            if (!ruta || !ruta.legs || ruta.legs.length === 0) {
              calcularConDistanceMatrix({
                origenGoogle,
                destinoGoogle,
                distanciaSatelital,
              });

              return;
            }

            const distanciaMetros = ruta.legs.reduce((total, leg) => {
              return total + Number(leg.distance?.value || 0);
            }, 0);

            if (!distanciaMetros) {
              calcularConDistanceMatrix({
                origenGoogle,
                destinoGoogle,
                distanciaSatelital,
              });

              return;
            }

            const distanciaDirectionsKm = distanciaMetros / 1000;

            console.log("DEBUG ENVÍO GOOGLE:", {
              distanciaDirectionsKm,
              distanciaSatelital,
            });

            if (
              distanciaSatelital <= 0.25 &&
              distanciaDirectionsKm > distanciaSatelital * 4
            ) {
              aplicarReglasEnvio(distanciaSatelital, "directa");
              return;
            }

            aplicarReglasEnvio(distanciaDirectionsKm, "directions");
          },
        );
      } catch (error) {
        console.error("Error usando DirectionsService:", error);

        calcularConDistanceMatrix({
          origenGoogle,
          destinoGoogle,
          distanciaSatelital,
        });
      }
    },
    [
      aplicarReglasEnvio,
      calcularConDistanceMatrix,
      resetearEnvio,
      latRestaurante,
      lngRestaurante,
    ],
  );

  return {
    costoEnvio,
    distanciaKm,
    mensajeEnvio,
    esRutaValida,
    fueraDeRango,
    calcularRuta,
    resetearEnvio,
  };
}

export default useCalculoEnvio;
